<template>
    <div class="slider">
        <div class="slider__carousel">
            <carousel
                ref="lCarousel"
                @init="calc"
                v-model="currentSlide"
                :wrap-around="wrapAround"
                :items-to-show="itemsToShow"
                :breakpoints="breakpoints"
                class="slider__fade-out"
            >
                <slide
                    v-for="(slide, index) of slides"
                    :key="slide"
                    class="slider__slide"
                    :class="{
                        'slider__slide--wrap-around': wrapAround,
                        'slider__slide--first': index == 0 && !wrapAround,
                        'slider__slide--last':
                            index == slides.length - 1 && !wrapAround,
                    }"
                    :style="{ 'z-index': slides.length - index }"
                >
                    <slot name="default" v-bind="slide" :index="index"></slot>
                </slide>
            </carousel>
        </div>
        <div class="slider__navigation d-flex flex-column">
            <input
                v-if="showPagination"
                v-model="currentSlide"
                type="range"
                id="slider"
                step="1"
                :min="minSlide"
                :max="maxSlide"
                @input="changeSlide"
                class="slider__pagination"
            />
            <div
                class="d-flex justify-content-between slider__controls"
                v-if="controls"
            >
                <FormButton
                    class="l-button--secondary l-button--icon-only slider__button"
                    :size40="true"
                    @click="prev"
                    :disabled="!wrapAround && currentSlide === minSlide"
                >
                    <IconsArrowLeft />
                </FormButton>
                <div v-if="showCurrent" class="equal-base equal-base--tighter">
                    <span class="slider__current">{{ currentSlide + 1 }}</span>
                    <span class="slider__count"> / {{ slides.length }}</span>
                </div>
                <FormButton
                    class="l-button--secondary l-button--icon-only slider__button"
                    :size40="true"
                    @click="next"
                    :disabled="!wrapAround && currentSlide === maxSlide"
                >
                    <IconsArrowRight />
                </FormButton>
            </div>
        </div>
    </div>
</template>

<script setup>
import 'vue3-carousel/dist/carousel.css';
import { Carousel, Slide } from 'vue3-carousel';
import { onMounted, ref } from 'vue';

const props = defineProps({
    current: {
        type: Number,
        default: 0,
    },
    showCurrent: {
        type: Boolean,
        default: false,
    },
    showPagination: {
        type: Boolean,
        default: false,
    },
    wrapAround: {
        type: Boolean,
        default: false,
    },
    itemsToShow: {
        type: Number,
        default: 4.8,
    },
    controls: {
        type: Boolean,
        default: true,
    },
    breakpoints: {
        type: Object,
        default: () => ({
            1: {
                itemsToShow: 1.8,
            },
            425: {
                itemsToShow: 2.8,
            },
            700: {
                itemsToShow: 3.8,
            },
            1024: {
                itemsToShow: 4.8,
            },
        }),
    },
    slides: {
        type: Object,
        required: true,
    },
});

const lCarousel = ref(null);
const currentSlide = ref(props.current);
const maxSlide = ref(0);
const minSlide = ref(0);
const thumbWidth = ref('50%');

function next() {
    lCarousel.value.next();
}

function prev() {
    lCarousel.value.prev();
}

const calc = () => {
    // timeout in order to get correct minSlide and maxSlide values
    minSlide.value = lCarousel.value.data.minSlide.value;
    maxSlide.value = lCarousel.value.data.maxSlide.value;
    currentSlide.value = minSlide.value;
    lCarousel.value.slideTo(minSlide.value);
    thumbWidth.value = `${100 / (maxSlide.value - minSlide.value + 1)}%`;
};

function changeSlide(event) {
    lCarousel.value.slideTo(event.target.value);
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';
@import '@/node_modules/bootstrap/scss/mixins';

.slider {
    &__navigation {
        display: flex;
        justify-content: space-between;
        margin-top: 15px;
    }

    &__carousel {
        position: relative;
    }
    .is-dragging {
        cursor: grabbing;
        .carousel__slide {
            pointer-events: none;
        }
    }

    &__slide {
        padding-right: 30px;
        padding-left: 0;

        &--wrap-around {
            padding-right: 15px !important;
            padding-left: 15px !important;
        }
    }

    &__slide-overflow {
        padding-left: 120px !important;

        @include media-breakpoint-down(1760) {
            padding-left: 60px !important;
        }

        @include media-breakpoint-down(880) {
            padding-left: $padding-container !important;
        }
    }

    &__controls {
        position: relative;
    }

    &__current {
        position: absolute;
        right: 50%;
        bottom: 50%;
        transform: translateX(-8px) translateY(50%);
    }

    &__count {
        position: absolute;
        left: 50%;
        bottom: 50%;
        transform: translateX(-2px) translateY(50%);
    }

    &__button {
        background-color: $color-dark-02;
        border-color: $color-dark-02;
    }

    &__pagination {
        padding-bottom: 15px;
    }

    &--fade-out {
        .slider__fade-out {
            overflow: hidden;

            &::before {
                pointer-events: none;
                z-index: 1;
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                height: 100%;
                width: 120px;
                background: linear-gradient(
                    to left,
                    rgba(0, 0, 0, 0) 0%,
                    rgba(0, 0, 0, 0.8) 70%,
                    rgba(0, 0, 0, 1) 100%
                );

                @include media-breakpoint-down(880) {
                    left: -60px;
                }

                @include media-breakpoint-down(560) {
                    left: -90px;
                }
            }

            &::after {
                pointer-events: none;
                content: '';
                position: absolute;
                top: 0;
                right: 0;
                height: 100%;
                width: 120px;
                background: linear-gradient(
                    to right,
                    rgba(0, 0, 0, 0) 0%,
                    rgba(0, 0, 0, 0.8) 70%,
                    rgba(0, 0, 0, 1) 100%
                );

                @include media-breakpoint-down(880) {
                    right: -60px;
                }

                @include media-breakpoint-down(560) {
                    right: -90px;
                }
            }
        }
    }

    &--overflow {
        :deep(.carousel) {
            margin-left: -120px;
            margin-right: -120px;

            @include media-breakpoint-down(1760) {
                margin-left: -60px;
                margin-right: -60px;
            }

            @include media-breakpoint-down(880) {
                margin-left: $padding-container-negative;
                margin-right: $padding-container-negative;
            }
        }

        :deep(.slider__slide--first) {
            padding-left: 120px !important;

            @include media-breakpoint-down(1760) {
                padding-left: 60px !important;
            }

            @include media-breakpoint-down(880) {
                padding-left: $padding-container !important;
            }
        }

        :deep(.slider__slide--last) {
            padding-right: 120px !important;

            @include media-breakpoint-down(1760) {
                padding-right: 60px !important;
            }

            @include media-breakpoint-down(880) {
                padding-right: $padding-container !important;
            }
        }
    }

    &--picker {
        :deep(.carousel__viewport) {
            overflow: unset;
        }

        .slider__fade-out {
            overflow: unset;
        }
    }
}

// Styling Cross-Browser Compatible Range Inputs with Sass
// Github: https://github.com/darlanrod/input-range-sass
// Author: Darlan Rod https://github.com/darlanrod
// Version 1.5.3
// MIT License

$track-color: #262626 !default;
$thumb-color: #333333 !default;

$thumb-radius: 2px !default;
$thumb-height: 2px !default;
$thumb-width: v-bind(thumbWidth) !default;
$thumb-shadow-size: 0px !default;
$thumb-shadow-blur: 0px !default;
$thumb-shadow-color: rgba(0, 0, 0, 0.2) !default;
$thumb-border-width: 0px !default;
$thumb-border-color: #000000 !default;

$track-width: 100% !default;
$track-height: 2px !default;
$track-shadow-size: 0px !default;
$track-shadow-blur: 0px !default;
$track-shadow-color: rgba(0, 0, 0, 0.2) !default;
$track-border-width: 0px !default;
$track-border-color: #000000 !default;

$track-radius: 2px !default;
$contrast: 5% !default;

$ie-bottom-track-color: darken($track-color, $contrast) !default;

@mixin shadow($shadow-size, $shadow-blur, $shadow-color) {
    box-shadow:
        $shadow-size $shadow-size $shadow-blur $shadow-color,
        0 0 $shadow-size lighten($shadow-color, 5%);
}

@mixin track {
    cursor: default;
    height: $track-height;
    transition: all 0.2s ease;
    width: $track-width;
}

@mixin thumb {
    @include shadow(
        $thumb-shadow-size,
        $thumb-shadow-blur,
        $thumb-shadow-color
    );
    background: $thumb-color;
    border: $thumb-border-width solid $thumb-border-color;
    border-radius: $thumb-radius;
    box-sizing: border-box;
    cursor: grab;
    height: $thumb-height;
    width: $thumb-width;
}

[type='range'] {
    -webkit-appearance: none;
    background: transparent;
    margin: calc($thumb-height / 2) 0;
    width: $track-width;

    &::-moz-focus-outer {
        border: 0;
    }

    &:focus {
        outline: 0;
    }

    &::-webkit-slider-runnable-track {
        @include track;
        @include shadow(
            $track-shadow-size,
            $track-shadow-blur,
            $track-shadow-color
        );
        background: $track-color;
        border: $track-border-width solid $track-border-color;
        border-radius: $track-radius;
    }

    &::-webkit-slider-thumb {
        @include thumb;
        -webkit-appearance: none;
        margin-top: (
            calc(
                ($track-border-width * -2 + $track-height) / 2 -
                    ($thumb-height / 2)
            )
        );
    }

    &::-moz-range-track {
        @include shadow(
            $track-shadow-size,
            $track-shadow-blur,
            $track-shadow-color
        );
        @include track;
        background: $track-color;
        border: $track-border-width solid $track-border-color;
        border-radius: $track-radius;
        height: calc($track-height / 2);
    }

    &::-moz-range-thumb {
        @include thumb;
    }

    &::-ms-track {
        @include track;
        background: transparent;
        border-color: transparent;
        border-width: calc($track-height / 2) 0;
        color: transparent;
    }

    &::-ms-fill-lower {
        @include shadow(
            $track-shadow-size,
            $track-shadow-blur,
            $track-shadow-color
        );
        background: $ie-bottom-track-color;
        border: $track-border-width solid $track-border-color;
        border-radius: ($track-radius * 2);
    }

    &::-ms-fill-upper {
        @include shadow(
            $track-shadow-size,
            $track-shadow-blur,
            $track-shadow-color
        );
        background: $track-color;
        border: $track-border-width solid $track-border-color;
        border-radius: ($track-radius * 2);
    }

    &::-ms-thumb {
        @include thumb;
        margin-top: calc($track-height / 4);
    }

    &:disabled {
        &::-webkit-slider-thumb,
        &::-moz-range-thumb,
        &::-ms-thumb,
        &::-webkit-slider-runnable-track,
        &::-ms-fill-lower,
        &::-ms-fill-upper {
            cursor: not-allowed;
        }
    }
}
</style>
